<template>
    <div class="tab-pane active">
        <div v-if="locationsListCopy && locationsListCopy.length > 0" class="table-wrapper no-print">
            <table
                v-show="!searchKeyword || filteredLocationsList.length"
                id="forAT_table-CheckInResidentCheckIn"
                class="common-table check-in-config"
            >
                <thead>
                    <tr class="tbl-header">
                        <th>
                            <div class="check-in-config-header-cell">
                                <span>{{ $t('checkIn.residentCheckInTab.table.columns.location') }}</span>
                            </div>
                        </th>
                        <th>
                            <div class="check-in-config-header-cell">
                                <span>{{ $t('checkIn.residentCheckInTab.table.columns.enabled') }}</span>
                                <button
                                    type="button"
                                    class="selectAll header-buttons"
                                    @click="toggleSelectAll"
                                >
                                    {{ selectAllButtonText }}
                                </button>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, index) in filteredLocationsList" :key="row.location_id">
                        <td>
                            {{
                                row.devices ? row.name.concat(' (' + row.devices.join(', ') + ')') : row.name
                            }}
                        </td>
                        <td>
                            <div>
                                <input
                                    :id="'forAT_CheckIn-config-presence-' + index"
                                    v-model="row.presence"
                                    type="checkbox"
                                    class="pointer"
                                    :class="{ checked: row.presence }"
                                />
                                <label :for="'forAT_CheckIn-config-presence-' + index"></label>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <h3 v-if="searchKeyword && !filteredLocationsList.length">
                {{ $t('common.searchNoResult', { val: searchKeyword }) }}
            </h3>
        </div>
        <div v-else class="data-empty no-print">
            <h3 v-if="dataLoaded" v-text="$t('checkIn.residentCheckInTab.noLocations')"></h3>
        </div>
        <div class="print-only">
            <h4 v-if="searchKeyword && filteredLocationsList.length > 0">
                {{ $t('print.search', { val: searchKeyword }) }}
            </h4>
            <table v-if="locationsListCopy.length > 0" class="table print-checkin">
                <thead>
                    <tr>
                        <th v-text="$t('checkIn.residentCheckInTab.table.columns.location')"></th>
                        <th v-text="$t('checkIn.residentCheckInTab.table.columns.enabled')"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(location, index) in filteredLocationsListCopy" :key="index">
                        <td>
                            {{
                                location.devices
                                    ? location.name.concat(' (' + location.devices.join(', ') + ')')
                                    : location.name
                            }}
                        </td>
                        <td><i v-if="location.presence" class="icon-checkmark"></i></td>
                    </tr>
                </tbody>
            </table>
            <div v-else class="data-empty">
                <h3>{{ $t('checkIn.residentCheckInTab.noLocations') }}</h3>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import mappingState from '../common/mappingState';
import { mapState, mapMutations } from 'vuex';
import cloneDeep from 'lodash-es/cloneDeep';
import { resourcePresence } from '../../api/resources';
import { getCancelToken } from '../../api';
import { errorHandler, sortLocations, ifIsNotCanceled } from '../common/helpers';
import { SUCCESS, ERROR, ROUTE_NAMES } from '../../constants';

export default {
    mixins: [mappingState],

    data() {
        return {
            locationsList: [],
            locationsListCopy: [],
            dataLoaded: false,
            errorInResponse: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState(['unsavedChanges']),

        filteredLocationsList() {
            let keyword = this.searchKeyword;
            return keyword
                ? this.locationsList.filter(
                      item =>
                          item.name.toLowerCase().indexOf(keyword) !== -1 ||
                          (item.devices || []).some(device => device.toLowerCase().indexOf(keyword) !== -1)
                  )
                : this.locationsList;
        },

        selectAllButtonText() {
            const selectAll = this.$t('checkIn.residentCheckInTab.table.columns.selectAll');
            const unselect = this.$t('checkIn.residentCheckInTab.table.columns.unselectAll');

            return this.isSelectAll ? unselect : selectAll;
        },

        isSelectAll() {
            return this.filteredLocationsList.every(location => {
                return location.presence;
            });
        },

        filteredLocationsListCopy() {
            let keyword = this.searchKeyword;
            let searched = this.locationsListCopy.filter(
                item =>
                    item.name.toLowerCase().indexOf(keyword) !== -1 ||
                    (item.devices || []).some(device => device.toLowerCase().indexOf(keyword) !== -1)
            );

            return keyword && searched.length > 0 ? searched : this.locationsListCopy;
        },
    },

    watch: {
        locationsList: {
            handler() {
                this.setUnsavedChanges(true);
            },
            deep: true,
        },
    },

    created() {
        this.updateData(() => {
            return this.refreshLocationsList();
        });
        this.$nextTick(() => {
            this.refreshLocationsList();
            this.configButton({
                buttonName: 'saveButton',
                action: () => this.updateLocationsList(),
                disabled: () => !this.unsavedChanges || this.errorInResponse,
            });
        });
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        ...mapMutations({ updateData: 'SET_UPDATE_DATA' }),

        refreshLocationsList() {
            this.showLoading(true);
            this.errorInResponse = false;
            this.locationsList = [];
            this.locationsListCopy = [];
            return resourcePresence({
                id: this.userAreaId,
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    this.locationsList = response.data ? sortLocations(response.data) : [];
                    this.locationsListCopy = cloneDeep(this.locationsList);
                    this.showLoading(false);
                    this.dataLoaded = true;
                    this.setSearchEnabled(this.locationsListCopy && this.locationsListCopy.length > 0);
                    this.$nextTick(() => {
                        this.setUnsavedChanges(false);
                    });
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            errorHandler(response.status, ROUTE_NAMES.CHECK_IN__NOT_CHECKED_IN);
                            this.setSearchEnabled(false);
                            this.$nextTick(() => {
                                this.setUnsavedChanges(false);
                            });
                            this.dataLoaded = true;
                        },
                    })
                );
        },

        updateLocationsList() {
            this.showLoading(true);
            return resourcePresence({
                method: 'post',
                id: this.userAreaId,
                data: JSON.stringify(this.locationsList),
            })
                .then(() => {
                    if (this.$router.currentRoute.name === ROUTE_NAMES.CHECK_IN__RESIDENT_CHECK_IN) {
                        this.refreshLocationsList();
                        this.showLoading(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('common.saved'),
                        });
                        this.$nextTick(() => {
                            this.setUnsavedChanges(false);
                        });
                    }
                })
                .catch(response => {
                    if (response.data && response.data.error) {
                        this.popup({
                            type: ERROR,
                            header: this.$t('common.error'),
                            text: this.$t(response.data.error),
                        });
                    }
                    if (response.status === 404) {
                        return this.refreshLocationsList().then(() => {
                            this.showLoading(false);
                            this.setUnsavedChanges(false);
                        });
                    } else if (response.status === 403) {
                        // Here if "Check-in" is disabled in NISM GUI (during saving the page data)

                        // Clear page
                        this.locationsList = [];

                        // Forget unsaved changes
                        this.locationsListCopy = [];
                        this.$nextTick(() => {
                            this.setUnsavedChanges(false);
                        });
                    }
                    this.showLoading(false);
                    return Promise.resolve();
                });
        },

        toggleSelectAll() {
            let newValue = !this.isSelectAll;

            this.filteredLocationsList.forEach(location => {
                location.presence = newValue;
            });
        },
    },
};
</script>
